import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface AccessInfo {
  email: string;
  password: string | null;
  userExisted: boolean;
  courseSlug: string;
}

const CienciaDeLograrAccess = () => {
  const [info, setInfo] = useState<AccessInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const run = async () => {
      if (sessionId) {
        // If we already fulfilled this session, reuse the stored credentials so
        // a refresh doesn't wipe the generated password.
        const cachedRaw = localStorage.getItem(`course_access_info_${sessionId}`);
        if (cachedRaw) {
          try {
            const cached: AccessInfo = JSON.parse(cachedRaw);
            setInfo(cached);
            sessionStorage.setItem("course_access_info", cachedRaw);
            return;
          } catch {
            // fall through and re-fulfill
          }
        }

        setLoading(true);
        try {
          const { data, error } = await supabase.functions.invoke(
            "fulfill-course-payment",
            { body: { sessionId } }
          );
          if (error) throw error;
          if (data?.error) throw new Error(data.error);

          const payload: AccessInfo = {
            email: data.email,
            password: data.password ?? null,
            userExisted: !!data.userExisted,
            courseSlug: data.courseSlug || "ciencia-de-lograr",
          };
          const serialized = JSON.stringify(payload);
          sessionStorage.setItem("course_access_info", serialized);
          localStorage.setItem(`course_access_info_${sessionId}`, serialized);
          setInfo(payload);
        } catch (e: any) {
          setErrorMsg(
            e.message || "No pudimos confirmar tu pago. Escríbenos y lo resolvemos."
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      const raw = sessionStorage.getItem("course_access_info");
      if (!raw) {
        navigate("/courses/ciencia-de-lograr/checkout");
        return;
      }
      setInfo(JSON.parse(raw));
    };
    run();
  }, [navigate, sessionId]);

  const copy = (txt: string) => {
    navigator.clipboard.writeText(txt);
    toast({ title: "Copiado al portapapeles" });
  };

  const handleAccessCourse = async () => {
    if (!info) return;
    setAutoLoggingIn(true);
    try {
      if (info.password) {
        await supabase.auth.signInWithPassword({
          email: info.email,
          password: info.password,
        });
      }
      navigate("/courses/ciencia-de-lograr/view");
    } catch {
      navigate("/auth/login");
    } finally {
      setAutoLoggingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
        <p className="text-gray-300">Confirmando tu pago...</p>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center max-w-xl">
          <h1 className="text-2xl font-bold mb-4">No pudimos confirmar tu acceso</h1>
          <p className="text-gray-300 mb-6">{errorMsg}</p>
          <a
            href="https://wa.me/18333104753?text=Pagu%C3%A9%20La%20Ciencia%20de%20Lograr%20y%20no%20puedo%20acceder"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-accent hover:bg-accent/80 text-black">
              Hablar con el equipo
            </Button>
          </a>
        </div>
      </div>
    );
  }

  if (!info) return null;


  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <Navbar />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <CheckCircle2 className="w-20 h-20 text-accent mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              ¡Inscripción Confirmada!
            </h1>
            <p className="text-gray-300 text-lg">
              Tu acceso al curso "La Ciencia de Lograr" está listo.
            </p>
          </div>

          <div className="bg-white/5 p-8 rounded-lg border border-white/10 space-y-5 mb-8">
            <h2 className="text-xl font-bold text-accent mb-4">
              Tus datos de acceso
            </h2>

            <div>
              <p className="text-sm text-gray-400 mb-1">Correo / Usuario</p>
              <div className="flex items-center gap-2 bg-black/40 px-4 py-3 rounded">
                <span className="flex-1 font-mono">{info.email}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(info.email)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {info.password ? (
              <div>
                <p className="text-sm text-gray-400 mb-1">
                  Contraseña (guárdala en lugar seguro)
                </p>
                <div className="flex items-center gap-2 bg-black/40 px-4 py-3 rounded">
                  <span className="flex-1 font-mono">{info.password}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copy(info.password!)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Puedes cambiar tu contraseña desde la opción "¿Olvidaste tu
                  contraseña?" en el login.
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-300 bg-accent/10 p-3 rounded">
                Ya tenías una cuenta con este correo. Usa tu contraseña habitual
                para entrar.
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleAccessCourse}
              disabled={autoLoggingIn}
              className="flex-1 bg-accent hover:bg-accent/80 text-black text-lg py-6"
            >
              {autoLoggingIn ? "Entrando..." : "Ver el curso ahora"}
            </Button>
            <Link to="/auth/login" className="flex-1">
              <Button variant="outline" className="w-full text-lg py-6">
                Ir al login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CienciaDeLograrAccess;
